import type { NurseWithRelation } from "@/types/nurse-type";
import {
  NurseSwapTables,
  type BucketPillOption,
} from "@/components/feature/nurse-swap/NurseSwapTables";
import { SelectItem } from "@/components/ui/select";
import { useMemo } from "react";

export type GradeBucket = {
  id: string;
  label: string;
  count?: number;
};

export type GradeTransferTablesProps = {
  /** 'total' 제외한 등급 행 */
  gradeBuckets: GradeBucket[];
  nursesByGrade: Record<string, NurseWithRelation[]>;
  unassignedNurses: NurseWithRelation[];
  selectedLeftGradeId: string | null;
  /** 우측 버킷: Grade 또는 미지정(`unassigned`). null이면 미선택 */
  selectedRightGradeId: string | null;
  onLeftGradeChange: (id: string | null) => void;
  onRightGradeChange: (id: string | null) => void;
  onMoveNurses?: (
    nurseIds: string[],
    fromGradeKey: string,
    toGradeKey: string
  ) => void;
  selectedGroupId: string | null;
  activeNurseIdForSideProfile?: string | null;
  onNurseClick?: (nurseId: string) => void;
  /** 인원 미달 등 — 버킷 pill 호버 시 (Shift 설정 모달 밖 Grade 영역 전용) */
  invalidGradeTooltip?: (gradeKey: string) => string | undefined;
};

const UNASSIGNED_KEY = "unassigned";

export function GradeTransferTables({
  gradeBuckets,
  nursesByGrade,
  unassignedNurses,
  selectedLeftGradeId,
  selectedRightGradeId,
  onLeftGradeChange,
  onRightGradeChange,
  onMoveNurses,
  selectedGroupId,
  activeNurseIdForSideProfile,
  onNurseClick,
  invalidGradeTooltip,
}: GradeTransferTablesProps) {
  const assignableBuckets = useMemo(
    () => gradeBuckets.filter((g) => g.id !== "total"),
    [gradeBuckets]
  );

  const baseLeftData = useMemo(() => {
    if (
      selectedLeftGradeId === UNASSIGNED_KEY ||
      selectedLeftGradeId === null
    ) {
      return unassignedNurses;
    }
    return nursesByGrade[selectedLeftGradeId] ?? [];
  }, [selectedLeftGradeId, unassignedNurses, nursesByGrade]);

  const baseRightData = useMemo(() => {
    if (selectedRightGradeId === null) return [];
    if (selectedRightGradeId === UNASSIGNED_KEY) return unassignedNurses;
    return nursesByGrade[selectedRightGradeId] ?? [];
  }, [selectedRightGradeId, unassignedNurses, nursesByGrade]);

  const leftLabel = useMemo(() => {
    if (
      selectedLeftGradeId === UNASSIGNED_KEY ||
      selectedLeftGradeId === null
    ) {
      return "미지정";
    }
    return (
      assignableBuckets.find((g) => g.id === selectedLeftGradeId)?.label ??
      selectedLeftGradeId
    );
  }, [selectedLeftGradeId, assignableBuckets]);

  const rightLabel = useMemo(() => {
    if (selectedRightGradeId === null) return "Grade 선택";
    if (selectedRightGradeId === UNASSIGNED_KEY) return "미지정";
    return (
      assignableBuckets.find((g) => g.id === selectedRightGradeId)?.label ??
      selectedRightGradeId
    );
  }, [selectedRightGradeId, assignableBuckets]);

  const leftDisplayText = useMemo(() => {
    if (
      selectedLeftGradeId === UNASSIGNED_KEY ||
      selectedLeftGradeId === null
    ) {
      return (
        <>
          미지정{" "}
          <span className="text-gray-500">({unassignedNurses.length}명)</span>
        </>
      );
    }
    const count = nursesByGrade[selectedLeftGradeId]?.length ?? 0;
    return (
      <>
        {leftLabel} <span className="text-gray-500">({count}명)</span>
      </>
    );
  }, [selectedLeftGradeId, unassignedNurses.length, nursesByGrade, leftLabel]);

  const rightDisplayText = useMemo(() => {
    if (selectedRightGradeId === null) return <>Grade 선택</>;
    if (selectedRightGradeId === UNASSIGNED_KEY) {
      return (
        <>
          미지정{" "}
          <span className="text-gray-500">({unassignedNurses.length}명)</span>
        </>
      );
    }
    const count = nursesByGrade[selectedRightGradeId]?.length ?? 0;
    return (
      <>
        {rightLabel} <span className="text-gray-500">({count}명)</span>
      </>
    );
  }, [
    selectedRightGradeId,
    nursesByGrade,
    rightLabel,
    unassignedNurses.length,
  ]);

  const leftSelectChildren = (
    <>
      <SelectItem
        value={UNASSIGNED_KEY}
        disabled={selectedRightGradeId === UNASSIGNED_KEY}
      >
        <div className="flex items-center justify-between w-full">
          <span>미지정</span>
          <span className="text-gray-500 ml-2 opacity-70">
            ({unassignedNurses.length}명)
          </span>
        </div>
      </SelectItem>
      {assignableBuckets.map((g) => {
        const cnt = nursesByGrade[g.id]?.length ?? 0;
        const disabled =
          selectedRightGradeId !== null && g.id === selectedRightGradeId;
        return (
          <SelectItem key={g.id} value={g.id} disabled={disabled}>
            <div className="flex items-center justify-between w-full">
              <span>{g.label}</span>
              <span className="text-gray-500 ml-2 opacity-70">({cnt}명)</span>
            </div>
          </SelectItem>
        );
      })}
    </>
  );

  const rightSelectChildren =
    assignableBuckets.length > 0 ? (
      <>
        <SelectItem
          value={UNASSIGNED_KEY}
          disabled={
            selectedLeftGradeId === UNASSIGNED_KEY ||
            selectedLeftGradeId === null
          }
        >
          <div className="flex items-center justify-between w-full">
            <span>미지정</span>
            <span className="text-gray-500 ml-2">
              ({unassignedNurses.length}명)
            </span>
          </div>
        </SelectItem>
        {assignableBuckets.map((g) => {
          const cnt = nursesByGrade[g.id]?.length ?? 0;
          const disabled =
            selectedLeftGradeId !== null &&
            selectedLeftGradeId !== UNASSIGNED_KEY &&
            g.id === selectedLeftGradeId;
          return (
            <SelectItem key={g.id} value={g.id} disabled={disabled}>
              <div className="flex items-center justify-between w-full">
                <span>{g.label}</span>
                <span className="text-gray-500 ml-2">({cnt}명)</span>
              </div>
            </SelectItem>
          );
        })}
      </>
    ) : (
      <></>
    );

  const leftValue =
    selectedLeftGradeId === UNASSIGNED_KEY || selectedLeftGradeId === null
      ? UNASSIGNED_KEY
      : selectedLeftGradeId;

  const noGrades = assignableBuckets.length === 0;

  /** Figma Grade 설정: Select 대신 162×40급 가로 pill */
  const leftBucketPills = useMemo((): BucketPillOption[] => {
    const rows: BucketPillOption[] = [
      {
        value: UNASSIGNED_KEY,
        disabled: selectedRightGradeId === UNASSIGNED_KEY,
        violationTooltip: invalidGradeTooltip?.(UNASSIGNED_KEY),
        label: (
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <span className="min-w-0 truncate font-medium">미지정</span>
            <span className="shrink-0 tabular-nums">
              {unassignedNurses.length}명
            </span>
          </span>
        ),
      },
    ];
    assignableBuckets.forEach((g) => {
      const cnt = nursesByGrade[g.id]?.length ?? 0;
      const disabled =
        selectedRightGradeId !== null && g.id === selectedRightGradeId;
      rows.push({
        value: g.id,
        disabled,
        violationTooltip: invalidGradeTooltip?.(g.id),
        label: (
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <span className="min-w-0 truncate font-medium">{g.label}</span>
            <span className="shrink-0 tabular-nums opacity-70">{cnt}명</span>
          </span>
        ),
      });
    });
    return rows;
  }, [
    assignableBuckets,
    nursesByGrade,
    selectedRightGradeId,
    unassignedNurses.length,
    invalidGradeTooltip,
  ]);

  const rightBucketPills = useMemo((): BucketPillOption[] => {
    const leftIsUnassigned =
      selectedLeftGradeId === UNASSIGNED_KEY || selectedLeftGradeId === null;
    const rows: BucketPillOption[] = [
      {
        value: UNASSIGNED_KEY,
        disabled: leftIsUnassigned,
        violationTooltip: invalidGradeTooltip?.(UNASSIGNED_KEY),
        label: (
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <span className="min-w-0 truncate font-medium">미지정</span>
            <span className="shrink-0 tabular-nums opacity-70">
              {unassignedNurses.length}명
            </span>
          </span>
        ),
      },
    ];
    assignableBuckets.forEach((g) => {
      const cnt = nursesByGrade[g.id]?.length ?? 0;
      const disabled =
        selectedLeftGradeId !== null &&
        selectedLeftGradeId !== UNASSIGNED_KEY &&
        g.id === selectedLeftGradeId;
      rows.push({
        value: g.id,
        disabled,
        violationTooltip: invalidGradeTooltip?.(g.id),
        label: (
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <span className="min-w-0 truncate font-medium">{g.label}</span>
            <span className="shrink-0 tabular-nums opacity-70">{cnt}명</span>
          </span>
        ),
      });
    });
    return rows;
  }, [
    assignableBuckets,
    nursesByGrade,
    selectedLeftGradeId,
    unassignedNurses.length,
    invalidGradeTooltip,
  ]);

  const effectiveLeftKey =
    selectedLeftGradeId === null || selectedLeftGradeId === UNASSIGNED_KEY
      ? UNASSIGNED_KEY
      : selectedLeftGradeId;
  const sameBucketSelected =
    selectedRightGradeId !== null && effectiveLeftKey === selectedRightGradeId;

  return (
    <NurseSwapTables
      selectedGroupId={selectedGroupId}
      activeNurseIdForSideProfile={activeNurseIdForSideProfile}
      onNurseClick={onNurseClick}
      disableMoveRight={
        selectedRightGradeId === null || noGrades || sameBucketSelected
      }
      disableMoveLeft={noGrades || sameBucketSelected}
      onMoveToRight={(selectedNurseIds) => {
        if (selectedNurseIds.length === 0 || selectedRightGradeId === null)
          return;
        const fromKey =
          selectedLeftGradeId === UNASSIGNED_KEY || selectedLeftGradeId === null
            ? UNASSIGNED_KEY
            : selectedLeftGradeId;
        onMoveNurses?.(selectedNurseIds, fromKey, selectedRightGradeId);
      }}
      onMoveToLeft={(selectedNurseIds) => {
        if (selectedNurseIds.length === 0 || selectedRightGradeId === null)
          return;
        const toKey =
          selectedLeftGradeId === UNASSIGNED_KEY || selectedLeftGradeId === null
            ? UNASSIGNED_KEY
            : selectedLeftGradeId;
        onMoveNurses?.(selectedNurseIds, selectedRightGradeId, toKey);
      }}
      left={{
        selectValue: leftValue,
        onSelectChange: (value) => {
          if (value === UNASSIGNED_KEY) {
            onLeftGradeChange(UNASSIGNED_KEY);
          } else {
            onLeftGradeChange(value);
          }
        },
        triggerDisplay: leftDisplayText,
        selectChildren: leftSelectChildren,
        bucketPillOptions: leftBucketPills,
        data: baseLeftData,
      }}
      right={{
        selectValue: selectedRightGradeId ?? "",
        onSelectChange: (value) => {
          if (value === "") {
            onRightGradeChange(null);
          } else if (value === UNASSIGNED_KEY) {
            onRightGradeChange(UNASSIGNED_KEY);
          } else {
            onRightGradeChange(value);
          }
        },
        triggerDisplay: rightDisplayText,
        selectChildren: rightSelectChildren,
        bucketPillOptions: noGrades ? undefined : rightBucketPills,
        data: baseRightData,
      }}
      rightBucketEmpty={noGrades}
      rightBucketEmptyHeader={
        <div className="text-gray-900 py-3.5 font-pretendard text-sm">
          등록된 Grade가 없습니다.
        </div>
      }
      rightBucketEmptyBody='상단 "Shift 인원 설정/관리"에서 Grade를 추가해 주세요.'
    />
  );
}
